import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  Stack,
  Typography,
  ImageListItem,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import WestOutlinedIcon from "@mui/icons-material/WestOutlined";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import dataConstant from "../../constants/dataConstants";
import urlConstant from "../../constants/urlConstants";
import { httpGet } from "../../services/commonService";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const Categories = ({ Id, onViewGalleryMedia }) => {
  const { id } = useParams();
  const [value, setValue] = useState(0);
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { accessToken, impersonateEmail } = useContext(AuthenticationContext);
  const url = `${urlConstant.MediaPage.GetAllMedialMenuById}?Id=${atob(id)}`;
  const [mediaMenuItems, setMediaMenuItems] = useState();
  const [mediaMenuTabs, setMediaMenuTabs] = useState([]);
  const [anchorElTab, setAnchorElTab] = useState(null);
  const { t } = useTranslation();
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const scrollStartX = useRef(0);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    scrollStartX.current = scrollContainerRef.current.scrollLeft;    
    document.body.style.userSelect = "none";
  };
  
  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStartX.current;
    scrollContainerRef.current.scrollLeft = scrollStartX.current - dx;
    checkScrollButtons();
  };
  
  const handleMouseUp = () => {
    isDragging.current = false;
    document.body.style.userSelect = "";
  };
  
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const el = scrollContainerRef.current;
    el.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      el.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const getResources = async (selectedId) => {
    await httpGet(url, true, true, accessToken, impersonateEmail)
      .then((response) => {
        const responseData = response.Value.Data;
        setMediaMenuTabs(responseData);
        const filterData = responseData.find((item) => item.Id === selectedId);
        setMediaMenuItems(filterData);
        const index = responseData.findIndex((item) => item.Id === selectedId);
        if (index !== -1) {
          setValue(index);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  useEffect(() => {
    if (Id) {
      getResources(Id);
    }
  }, [Id]);

  const handleTabClick = (event) => {
    setAnchorElTab(event.currentTarget);
  };

  const handleTabClose = (index) => {
    setAnchorElTab(null);
    if (typeof index === "number") {
      setValue(index);
      const selectedTabId = mediaMenuTabs[index]?.Id;
      if (selectedTabId) {
        getResources(selectedTabId);
      }
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    const selectedTabId = mediaMenuTabs[newValue]?.Id;
    if (selectedTabId) {
      getResources(selectedTabId);
    }
  };
  const checkScrollButtons = () => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const scrollLeft = Math.ceil(el.scrollLeft);
    const scrollWidth = Math.ceil(el.scrollWidth);
    const clientWidth = Math.ceil(el.clientWidth);
    setCanScrollLeft(scrollLeft > 0);
    const nearEnd = scrollLeft + clientWidth >= scrollWidth - 1;
    setCanScrollRight(!nearEnd);
  };

  const handleScroll = (direction) => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const scrollAmount = 200;
    el.scrollBy({
      left: direction === "right" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });
    setTimeout(() => {
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 2) {
        el.scrollLeft = el.scrollWidth;
      }
      checkScrollButtons();
    }, 500);
  };

  const tabRefs = useRef([]);
  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    const tabEl = tabRefs.current[value];

    if (scrollEl && tabEl) {
      const tabLeft = tabEl.offsetLeft;
      const tabRight = tabLeft + tabEl.offsetWidth;
      const visibleLeft = scrollEl.scrollLeft;
      const visibleRight = visibleLeft + scrollEl.clientWidth;

      // Scroll left if tab is cut on the left
      if (tabLeft < visibleLeft) {
        scrollEl.scrollTo({
          left: tabLeft,
          behavior: "smooth",
        });
      }
      // Scroll right if tab is cut on the right
      else if (tabRight > visibleRight) {
        scrollEl.scrollTo({
          left: tabRight - scrollEl.clientWidth + tabLeft / 2, // add small padding
          behavior: "smooth",
        });
      }

      // Re-check after animation
      setTimeout(checkScrollButtons, 500);
    }
  }, [value, mediaMenuTabs]);

  return (
    <>
      <Box flexDirection={"column"} gap={2} display={"flex"}>
        <Box width={"100%"}>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Stack gap={"20px"}>              
              <Stack
                direction={"row"}
                alignItems={"center"}
                width={"100%"}
                gap={"10px"}
                position={"relative"}
                sx={{ display: { xs: "flex", sm: "none" } }}
                mb={1}
                justifyContent="space-between"
              >
                {mediaMenuTabs[value] && (
                  <Typography
                    variant="h5"
                    sx={{
                      ml: 1,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 500,
                    }}
                  >
                    {mediaMenuTabs[value].Title}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleTabClick}
                  sx={{
                    px: 0.8,
                    py: 1,
                    minWidth: "40px",
                    minHeight: "40px",
                    ml: "auto",
                  }}
                >
                  <MoreVertIcon />
                </Button>
              </Stack>
              <Stack className="tabs-common secondary-design" sx={{ display: { xs: "none", sm: "flex" } }}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  width={"100%"}
                  gap={"10px"}
                  position={"relative"}
                >
                  {canScrollLeft && (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => handleScroll("left")}
                      sx={{
                        px: 2,
                        py: 2,                        
                        margin: 0,
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                        display: { md: "flex" },
                      }}
                      startIcon={<WestOutlinedIcon />}
                    />
                  )}
                  <Box
                    ref={scrollContainerRef}
                    sx={{
                      display: {
                        md: "flex",
                        overflow: "auto",
                        width: "calc(100% - 60px)",
                        scrollbarWidth: "none",
                      },

                      width: "100%",
                      scrollbarWidth: "none",
                      " &::-webkit-scrollbar": {
                        display: "none",
                      },
                    }}
                    style={{ cursor: "grab" }}
                  >
                    <Tabs
                      value={value}
                      onChange={handleChange}
                      aria-label="media tabs"
                      variant="scrollable"
                      sx={{
                        minWidth: "max-content",
                      }}
                    >
                      {mediaMenuTabs.map((tab, index) => (
                        <Tab
                          key={tab.Id}
                          label={tab.Title}
                          sx={{ maxWidth: "fit-content" }}
                          {...a11yProps(index)}
                          ref={(el) => (tabRefs.current[index] = el)}
                        />
                      ))}
                    </Tabs>
                  </Box>
                  {canScrollRight && (
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{
                        px: 2,
                        py: 2,
                        margin: 0,
                        ml: "auto",
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                        display: { md: "flex" },
                      }}
                      onClick={() => handleScroll("right")}
                      startIcon={<EastOutlinedIcon />}
                    />
                  )}
                </Stack>
              </Stack>
              <Stack
                direction={"row"}
                gap={"20px"}
                width={"100%"}
                alignItems={"center"}
                justifyContent={"space-between"}
                flexWrap={"wrap"}
              >
                <Stack
                  direction={"row"}
                  gap={1}
                  width={"100%"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  flexWrap={"wrap"}
                >
                  <Typography
                    color="secondary.light"
                    variant="h4"
                    fontWeight={600}
                  >
                    {t("Media.All Categories")}
                  </Typography>
                  <Stack
                    sx={{
                      flexDirection: "row",
                      flexWrap: {
                        xs: "wrap",
                        sm: "nowrap",
                      },
                    }}
                    gap={2}
                  >
                    <Stack
                      className="customfield-label-left"
                      sx={{
                        maxWidth: "700px",
                        width: "100%",
                        flexDirection: "row",
                        alignItems: "flex-end",
                      }}
                    ></Stack>
                  </Stack>
                </Stack>
                <Stack gap={"20px"} width={"100%"}>  
                  {mediaMenuItems?.TextBlogCount > 0 && ( 
                    <Card
                      sx={(theme) => ({
                        p: "20px",
                        background: theme.palette.common.black,
                        borderRadius: "20px",
                        height: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      })}
                    >
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={{ xs: "10px", sm: "20px" }}
                      >
                        <Card
                          sx={(theme) => ({
                            p: "10px",
                            background: theme.palette.primary.darker,
                            borderRadius: "10px",
                            width: { xs: "40px", sm: "70px" },
                            display: "inline-flex",
                            aspectRatio: 1,
                          })}
                        >
                          <ImageListItem
                            sx={{ "& img": { objectFit: "contain !important" } }}
                          >
                            <img src="/icons/link.svg" alt="" />
                          </ImageListItem>
                        </Card>
                        <Typography
                          variant="h4"
                          fontWeight={500}
                          color="secondary.light"
                          sx={{ fontSize: { xs: "16px", sm: "24px" } }}
                        >
                          {t("Media.TextBlog")}
                        </Typography>
                      </Stack>
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={{ xs: "10px", sm: "20px" }}
                      >
                        <Typography
                          variant="body1"
                          fontWeight={400}
                          color="secondary.text"
                          sx={{ fontSize: { xs: "12px", sm: "1rem" } }}
                        >
                          {mediaMenuItems?.TextBlogCount ?? 0}{" "}
                          {t("Media.in Total")}
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          sx={{
                            px: 1,
                            py: 1.5,
                            margin: 0,
                            "& .MuiButton-startIcon": {
                              margin: 0,
                            },
                          }}
                          startIcon={<EastOutlinedIcon />}
                          onClick={() =>
                            onViewGalleryMedia(
                              mediaMenuItems.Id,
                              mediaMenuItems.MediaId,
                              dataConstant.MediaCategoryTypes.TextBlog
                            )
                          }
                        />
                      </Stack>
                    </Card>               
                  )}       
                  {mediaMenuItems?.VideoCount > 0 && (            
                    <Card
                      sx={(theme) => ({
                        p: "20px",
                        background: theme.palette.common.black,
                        borderRadius: "20px",
                        height: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      })}
                    >
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={{ xs: "10px", sm: "20px" }}
                      >
                        <Card
                          sx={(theme) => ({
                            p: "10px",
                            background: theme.palette.primary.darker,
                            borderRadius: "10px",
                            width: { xs: "40px", sm: "70px" },
                            display: "inline-flex",
                            aspectRatio: 1,
                          })}
                        >
                          <ImageListItem
                            sx={{ "& img": { objectFit: "contain !important" } }}
                          >
                            <img src="/icons/play.svg" alt="" />
                          </ImageListItem>
                        </Card>
                        <Typography
                          variant="h4"
                          fontWeight={500}
                          color="secondary.light"
                          sx={{ fontSize: { xs: "16px", sm: "24px" } }}
                        >
                          {t("Media.Videos")}
                        </Typography>
                      </Stack>
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={{ xs: "10px", sm: "20px" }}
                      >
                        <Typography
                          variant="body1"
                          fontWeight={400}
                          color="secondary.text"
                          sx={{ fontSize: { xs: "12px", sm: "1rem" } }}
                        >
                          {mediaMenuItems?.VideoCount ?? 0} {t("Media.in Total")}
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          sx={{
                            px: 1,
                            py: 1.5,
                            margin: 0,
                            "& .MuiButton-startIcon": {
                              margin: 0,
                            },
                          }}
                          startIcon={<EastOutlinedIcon />}
                          onClick={() =>
                            onViewGalleryMedia(
                              mediaMenuItems.Id,
                              mediaMenuItems.MediaId,
                              dataConstant.MediaCategoryTypes.Video
                            )
                          }
                        />
                      </Stack>
                    </Card>
                  )}
                  {mediaMenuItems?.PictureCount > 0 && (
                    <Card
                      sx={(theme) => ({
                        p: "20px",
                        background: theme.palette.common.black,
                        borderRadius: "20px",
                        height: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      })}
                    >
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={{ xs: "10px", sm: "20px" }}
                      >
                        <Card
                          sx={(theme) => ({
                            p: "10px",
                            background: theme.palette.primary.darker,
                            borderRadius: "10px",
                            width: { xs: "40px", sm: "70px" },
                            display: "inline-flex",
                            aspectRatio: 1,
                          })}
                        >
                          <ImageListItem
                            sx={{ "& img": { objectFit: "contain !important" } }}
                          >
                            <img src="/icons/gallery.svg" alt="" />
                          </ImageListItem>
                        </Card>
                        <Typography
                          variant="h4"
                          fontWeight={500}
                          color="secondary.light"
                          sx={{ fontSize: { xs: "16px", sm: "24px" } }}
                        >
                          {t("Media.Images")}
                        </Typography>
                      </Stack>
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={{ xs: "10px", sm: "20px" }}
                      >
                        <Typography
                          variant="body1"
                          fontWeight={400}
                          color="secondary.text"
                          sx={{ fontSize: { xs: "12px", sm: "1rem" } }}
                        >
                          {mediaMenuItems?.PictureCount ?? 0}{" "}
                          {t("Media.in Total")}
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          sx={{
                            px: 1,
                            py: 1.5,
                            margin: 0,
                            "& .MuiButton-startIcon": {
                              margin: 0,
                            },
                          }}
                          startIcon={<EastOutlinedIcon />}
                          onClick={() =>
                            onViewGalleryMedia(
                              mediaMenuItems.Id,
                              mediaMenuItems.MediaId,
                              dataConstant.MediaCategoryTypes.Picture
                            )
                          }
                        />
                      </Stack>
                    </Card>
                  )}
                  {mediaMenuItems?.PdfCount > 0 && (
                    <Card
                        sx={(theme) => ({
                            p: "20px",
                            background: theme.palette.common.black,
                            borderRadius: "20px",
                            height: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                        })}
                    >
                        <Stack
                            direction={"row"}
                            alignItems={"center"}
                            gap={{ xs: "10px", sm: "20px" }}
                        >
                            <Card
                                sx={(theme) => ({
                                    p: "10px",
                                    background: theme.palette.primary.darker,
                                    borderRadius: "10px",
                                    width: { xs: "40px", sm: "70px" },
                                    display: "inline-flex",
                                    aspectRatio: 1,
                                })}
                            >
                              <ImageListItem
                                sx={{ "& img": { objectFit: "contain !important" } }}
                              >
                                <img src="/icons/pdf.svg" alt="" />
                              </ImageListItem>
                            </Card>
                            <Typography
                                variant="h4"
                                fontWeight={500}
                                color="secondary.light"
                                sx={{ fontSize: { xs: "16px", sm: "24px" } }}
                            >
                                {t("Media.PDF")}
                            </Typography>
                        </Stack>
                        <Stack
                            direction={"row"}
                            alignItems={"center"}
                            gap={{ xs: "10px", sm: "20px" }}
                        >
                            <Typography
                                variant="body1"
                                fontWeight={400}
                                color="secondary.text"
                                sx={{ fontSize: { xs: "12px", sm: "1rem" } }}
                            >
                                {mediaMenuItems?.PdfCount ?? 0} {t("Media.in Total")}
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{
                                    px: 1,
                                    py: 1.5,
                                    margin: 0,
                                    "& .MuiButton-startIcon": {
                                        margin: 0,
                                    },
                                }}
                                startIcon={<EastOutlinedIcon />}
                                onClick={() =>
                                    onViewGalleryMedia(
                                        mediaMenuItems.Id,
                                        mediaMenuItems.MediaId,
                                        dataConstant.MediaCategoryTypes.Pdf
                                    )
                                }
                            />
                        </Stack>
                    </Card>
                  )}
                  {( mediaMenuItems &&
                    !mediaMenuItems?.TextBlogCount &&
                    !mediaMenuItems?.VideoCount &&
                    !mediaMenuItems?.PictureCount &&
                    !mediaMenuItems?.PdfCount) && (
                    <Alert
                      sx={(theme) => ({
                        background: theme.palette.common.errorbg,
                        color: theme.palette.common.errortext,
                        marginBottom: "5px",
                        width: "100%",
                      })}
                      variant="filled"
                      severity="info"
                    >
                      {t("Media.No media available")}
                    </Alert>
                  )}  
                </Stack>
              </Stack>
            </Stack>
          </Card>
        </Box>
      </Box>
      <Menu
      anchorEl={anchorElTab}
      open={Boolean(anchorElTab)}
      onClose={() => handleTabClose()}
      sx={{ display: { xs: "block", sm: "none" } }}
      PaperProps={{
        sx: {
          maxHeight: 320,
          width: 250,
          overflowY: "auto",
        },
      }}
      >
        {mediaMenuTabs.map((tab, index) => (
          <MenuItem
            key={tab.Id}
            selected={index === value}
            onClick={() => handleTabClose(index)}
          >
            {tab.Title}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
export default Categories;